/* eslint-disable camelcase */
import { env } from "env";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

// Constants
import {
  STORAGE_CURRENCY_FROM_LINK,
  STORAGE_LANGUAGE_NAME,
  STORAGE_USER_COUNTRY,
} from "constants/variables";

// Selectors
import * as authSelectors from "redux/auth/selectors";
import * as userSelectors from "redux/user/selectors";

// Stores
import { useAuthStore } from "stores/auth";
import { useCountriesStore } from "stores/countries";

// Utils
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { useChatWootStore } from "stores/chatwoot";
import { ChatwootCustomAttributes, ChatwootUser } from "types/chatwoot";
import { getLocalStorageItem } from "utils/localStorage";
import ChatwootLoading from "../ChatwootLoading";

const DATE_FORMAT = "DD/MM/YYYY HH:mm";

const ChatwootWidget = () => {
  const { VITE_APP_PROJECT_NAME } = import.meta.env;
  const location = useLocation();
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const userData = useSelector(userSelectors.all);
  const countryCode = useCountriesStore((state) => state.countryCode);
  const currentCountry = localStorage.getItem(STORAGE_USER_COUNTRY);
  const currentLanguage = localStorage.getItem(STORAGE_LANGUAGE_NAME);

  const isInit = useChatWootStore((state) => state.isInit);
  const isLoading = useChatWootStore((state) => state.isLoading);
  const setIsLoading = useChatWootStore((state) => state.setIsLoading);

  const isGamePage = useMemo(
    () => location.pathname.includes("game"),
    [location],
  );

  const scriptTagRef = useRef<Nullable<HTMLScriptElement>>(null);
  const scriptReferenceRef = useRef<Nullable<HTMLScriptElement>>(null);

  const isAfterAuthCheck = useAuthStore((state) => state.isAfterAuthCheck);

  const [token, setToken] = useState("");
  const [hmacToken, setHmacToken] = useState("");

  const addTag = () => {
    const BASE_URL = env.VITE_APP_CHATWOOT_URL;
    const SDK_PATH = env.VITE_APP_CHATWOOT_SDK_PATH;
    scriptTagRef.current = document.createElement("script");
    // eslint-disable-next-line prefer-destructuring
    scriptReferenceRef.current = document.getElementsByTagName("script")[0];
    if (scriptTagRef.current && scriptReferenceRef.current) {
      // scriptTagRef.current.src = `${BASE_URL}/packs/js/sdk.js`;
      scriptTagRef.current.src = `${BASE_URL}${SDK_PATH || "/packs/js/sdk.js"}`;
      scriptReferenceRef.current.parentNode?.insertBefore(
        scriptTagRef.current,
        scriptReferenceRef.current,
      );
      scriptTagRef.current.async = !0;
      scriptTagRef.current.onload = () => {
        // eslint-disable-next-line no-console
        console.log("ONLOAD", BASE_URL, token, isInit);
        // if (
        //   window.$chatwoot &&
        //   !window.$chatwoot.isOpen &&
        //   !window.$chatwoot.hasLoaded
        // ) {
        //   return null;
        // }

        (window as any).chatwootSDK.run({
          websiteToken: token,
          baseUrl: BASE_URL,
        });

        if (window.$chatwoot) {
          if (userData.id) {
            const user: ChatwootUser = {
              name: userData.name,
              email: userData.login,
              identifier_hash: hmacToken,
            };
            const customAttributes: ChatwootCustomAttributes = {
              id: userData.id,
              name: userData.name,
              email: userData.login,
              identifier_hash: hmacToken,
              registered: dayjs(userData.registered).format(DATE_FORMAT),
              currency: userData.currency,
              country: userData.country,
              language: currentLanguage,
              bonuses: userData.bonuses?.toString(),
              balance: userData.balance?.toString(),
              rawData: {
                ...userData,
                registered: dayjs(userData.registered).format(DATE_FORMAT),
              },
            };
            window.$chatwoot.setUser(userData.id, user);
            window.$chatwoot.setCustomAttributes(customAttributes);
          } else {
            const currencyFromLink = getLocalStorageItem(
              STORAGE_CURRENCY_FROM_LINK,
            );
            const customAttributes: ChatwootCustomAttributes = {
              identifier_hash: hmacToken,
              currency: currencyFromLink || "",
              language: currentLanguage,
            };
            window.$chatwoot.setCustomAttributes(customAttributes);
          }

          if (window.$chatwoot) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            window.addEventListener("chatwoot:close", onClose);
          }
        }
        return false;
      };
      scriptTagRef.current.onerror = (error) => {
        // eslint-disable-next-line no-console
        console.log("Chatwood onerror", error);
      };
    }
  };

  const onReady = () => {
    if (userData.id) {
      const user: ChatwootUser = {
        name: userData.name,
        email: userData.login,
        identifier_hash: null,
      };
      const customAttributes: ChatwootCustomAttributes = {
        id: userData.id,
        name: userData.name,
        email: userData.login,
        registered: dayjs(userData.registered).format(DATE_FORMAT),
        currency: userData.currency,
        country: userData.country,
        language: currentLanguage,
        bonuses: userData.bonuses?.toString(),
        balance: userData.balance?.toString(),
        rawData: {
          ...userData,
          registered: dayjs(userData.registered).format(DATE_FORMAT),
        },
      };
      if (hmacToken) {
        user.identifier_hash = hmacToken;
        customAttributes.identifier_hash = hmacToken;
      }
      window.$chatwoot.setUser(userData.id, user);
      window.$chatwoot.setCustomAttributes(customAttributes);
    } else {
      const currencyFromLink = getLocalStorageItem(STORAGE_CURRENCY_FROM_LINK);
      const customAttributes: ChatwootCustomAttributes = {
        identifier_hash: hmacToken,
        currency: currencyFromLink || "",
        language: currentLanguage,
      };
      window.$chatwoot.setCustomAttributes(customAttributes);
    }
    if (isLoading) {
      setIsLoading(false);
      window.$chatwoot.toggle("open");
    }
  };

  const onClose = (_: any) => {
    // eslint-disable-next-line no-console
    console.log("ERROR chatwoot");
    window.$chatwoot.toggle("close");
    window.$chatwoot.toggleBubbleVisibility("hide");
    const chatwootWidgetHolder = document.querySelector(
      ".woot-widget-holder",
    ) as HTMLElement;
    if (chatwootWidgetHolder) {
      chatwootWidgetHolder.style.setProperty("z-index", "1", "important");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (isInit) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      setTokenForChat();
      const currencyFromLink = getLocalStorageItem(STORAGE_CURRENCY_FROM_LINK);

      if (isAuthenticated) {
        const iframeTag = document.getElementById(
          "chatwoot_live_chat_widget",
        ) as HTMLIFrameElement;
        if (iframeTag) {
          const url = new URL(iframeTag.src);
          url.searchParams.set("website_token", token);
          iframeTag.src = url.toString();
        }
      }

      if (
        !isAuthenticated &&
        currentCountry !== "undefined" &&
        !currencyFromLink &&
        countryCode
      ) {
        const iframeTag = document.getElementById(
          "chatwoot_live_chat_widget",
        ) as HTMLIFrameElement;
        if (iframeTag) {
          const url = new URL(iframeTag.src);
          url.searchParams.set("website_token", token);
          iframeTag.src = url.toString();
        }
      }

      const chatwootWidgetHolder = document.querySelector(
        ".woot-widget-holder",
      ) as HTMLElement;

      // если находимся на странице игры, то чат становится невидимым
      if (chatwootWidgetHolder) {
        if (isGamePage) {
          chatwootWidgetHolder.style.setProperty("opacity", "0");
        } else {
          chatwootWidgetHolder.style.setProperty("opacity", "1");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, countryCode, isInit, isGamePage]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const setTokenForChat = () => {
    if (VITE_APP_PROJECT_NAME === "banger") {
      if (
        userData &&
        isAuthenticated &&
        !!userData.country &&
        isAfterAuthCheck
      ) {
        if (userData.country === "France") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_FR);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_FR);
        } else if (userData.country === "United Kingdom") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_GB);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_GB);
        } else if (userData.country === "Bangladesh") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_BN);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_BN);
        } else if (userData.country === "India") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_IN);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_IN);
        } else if (userData.country === "Vietnam") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_VN);
          setHmacToken(env.VITE_APP_CHATWOOT_TOKEN_HMAC_BANGER_VN);
        } else {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER);
        }
        return;
      }
      if (!isAuthenticated) {
        const currencyFromLink = getLocalStorageItem(
          STORAGE_CURRENCY_FROM_LINK,
        );
        if (
          countryCode &&
          !userData.country &&
          currentCountry !== "undefined" &&
          !currencyFromLink &&
          isAfterAuthCheck
        ) {
          if (countryCode === "BN") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_BN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_BN);
          } else if (countryCode === "FR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_FR);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_FR);
          } else if (countryCode === "GB") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_GB);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_GB);
          } else if (countryCode === "IN") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_IN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_IN);
          } else if (countryCode === "VN") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_VN);
            setHmacToken(env.VITE_APP_CHATWOOT_TOKEN_HMAC_BANGER_VN);
          } else {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER);
          }
          return;
        }
        if (
          !userData.country &&
          currentCountry !== "undefined" &&
          currencyFromLink &&
          isAfterAuthCheck &&
          !countryCode
        ) {
          if (currencyFromLink === "EUR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_FR);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_FR);
          } else if (currencyFromLink === "GBP") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_GB);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_GB);
          } else if (currencyFromLink === "BDT") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_BN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_BN);
          } else if (currencyFromLink === "INR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_IN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_IN);
          } else if (currencyFromLink === "VND") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_VN);
            setHmacToken(env.VITE_APP_CHATWOOT_TOKEN_HMAC_BANGER_VN);
          } else {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER);
          }
          return;
        }
        if (
          !userData.country &&
          currentCountry !== "undefined" &&
          !currencyFromLink &&
          isAfterAuthCheck &&
          !countryCode
        ) {
          if (currentCountry === "BN") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_BN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_BN);
          } else if (currentCountry === "FR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_FR);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_FR);
          } else if (currentCountry === "GB") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_GB);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_GB);
          } else if (currentCountry === "IN") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_IN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER_IN);
          } else if (currentCountry === "VN") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER_VN);
            setHmacToken(env.VITE_APP_CHATWOOT_TOKEN_HMAC_BANGER_VN);
          } else {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER);
          }
          return;
        }
        if (
          !userData.country &&
          currentCountry === "undefined" &&
          isAfterAuthCheck &&
          !countryCode
        ) {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_BANGER);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BANGER);
        }
      }
    } else {
      // ------------------- GLORY -------------------
      if (
        userData &&
        isAuthenticated &&
        !!userData.country &&
        isAfterAuthCheck
      ) {
        if (userData.country === "Uzbekistan") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_UZ);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_UZ);
        } else if (userData.country === "Kenya") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_KE);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KE);
        } else if (userData.country === "Sri Lanka") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_SI);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_SI);
        } else if (userData.country === "Kazakhstan") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_KZ);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KZ);
        } else if (userData.country === "India") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_IN);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_IN);
        } else if (userData.country === "Turkey") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_TR);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_TR);
        } else if (userData.country === "Monaco") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_MC);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_MC);
        } else if (userData.country === "Bangladesh") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_BN);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BN);
        } else if (userData.country === "Azerbaijan") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_AZ);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_AZ);
        } else if (userData.country === "Nepal") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_NP);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_NP);
        } else if (userData.country === "Vietnam") {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_VN);
          setHmacToken(env.VITE_APP_CHATWOOT_TOKEN_HMAC_VN);
        } else if (userData.country.includes("Korea")) {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_KR);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KR);
        } else if (userData.country.includes("Pakistan")) {
          setToken(env.VITE_APP_CHATWOOT_TOKEN_PK);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC_PK);
        } else {
          setToken(env.VITE_APP_CHATWOOT_TOKEN);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC);
        }
        return;
      }
      if (!isAuthenticated) {
        const currencyFromLink = getLocalStorageItem(
          STORAGE_CURRENCY_FROM_LINK,
        );
        if (
          countryCode &&
          !userData.country &&
          currentCountry !== "undefined" &&
          !currencyFromLink &&
          isAfterAuthCheck
        ) {
          if (countryCode === "UZ") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_UZ);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_UZ);
          } else if (countryCode === "KE") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_KE);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KE);
          } else if (countryCode === "LK") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_SI);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_SI);
          } else if (countryCode === "KZ") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_KZ);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KZ);
          } else if (countryCode === "IN") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_IN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_IN);
          } else if (countryCode === "TR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_TR);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_TR);
          } else if (countryCode === "MC") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_MC);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_MC);
          } else if (countryCode === "BD") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BN);
          } else if (countryCode === "AZ") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_AZ);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_AZ);
          } else if (countryCode === "NP") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_NP);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_NP);
          } else if (countryCode === "VN") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_VN);
            setHmacToken(env.VITE_APP_CHATWOOT_TOKEN_HMAC_VN);
          } else if (countryCode === "KR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_KR);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KR);
          } else if (countryCode === "PK") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_PK);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_PK);
          } else {
            setToken(env.VITE_APP_CHATWOOT_TOKEN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC);
          }
          return;
        }
        if (
          !userData.country &&
          currentCountry !== "undefined" &&
          currencyFromLink &&
          isAfterAuthCheck &&
          !countryCode
        ) {
          if (currencyFromLink === "KES") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_KE);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KE);
          } else if (currencyFromLink === "UZS") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_UZ);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_UZ);
          } else if (currencyFromLink === "LKR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_SI);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_SI);
          } else if (currencyFromLink === "KZT") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_KZ);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KZ);
          } else if (currencyFromLink === "INR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_IN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_IN);
          } else if (currencyFromLink === "TRY") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_TR);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_TR);
          } else if (currencyFromLink === "BDT") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BN);
          } else if (currencyFromLink === "AZN") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_AZ);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_AZ);
          } else if (currencyFromLink === "NPR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_NP);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_NP);
          } else if (currencyFromLink === "VND") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_VN);
            setHmacToken(env.VITE_APP_CHATWOOT_TOKEN_HMAC_VN);
          } else if (currencyFromLink === "KRW") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_KR);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KR);
          } else if (currencyFromLink === "PKR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_PK);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_PK);
          } else {
            setToken(env.VITE_APP_CHATWOOT_TOKEN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC);
          }
          return;
        }
        if (
          !userData.country &&
          currentCountry !== "undefined" &&
          !currencyFromLink &&
          isAfterAuthCheck &&
          !countryCode
        ) {
          if (currentCountry === "UZ") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_UZ);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_UZ);
          } else if (currentCountry === "KE") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_KE);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KE);
          } else if (currentCountry === "LK") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_SI);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_SI);
          } else if (currentCountry === "KZ") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_KZ);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KZ);
          } else if (currentCountry === "IN") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_IN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_IN);
          } else if (currentCountry === "TR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_TR);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_TR);
          } else if (currentCountry === "MC") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_MC);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_MC);
          } else if (currentCountry === "BD") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_BN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_BN);
          } else if (currentCountry === "AZ") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_AZ);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_AZ);
          } else if (currentCountry === "NP") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_NP);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_NP);
          } else if (currentCountry === "VN") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_VN);
            setHmacToken(env.VITE_APP_CHATWOOT_TOKEN_HMAC_VN);
          } else if (currentCountry === "KR") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_KR);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_KR);
          } else if (currentCountry === "PK") {
            setToken(env.VITE_APP_CHATWOOT_TOKEN_PK);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC_PK);
          } else {
            setToken(env.VITE_APP_CHATWOOT_TOKEN);
            setHmacToken(env.VITE_APP_CHATWOOT_HMAC);
          }
          return;
        }
        if (
          !userData.country &&
          currentCountry === "undefined" &&
          isAfterAuthCheck &&
          !countryCode
        ) {
          setToken(env.VITE_APP_CHATWOOT_TOKEN);
          setHmacToken(env.VITE_APP_CHATWOOT_HMAC);
        }
      }
    }
  };

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("isInit, token", isInit, token);
    if (isInit && token) {
      // eslint-disable-next-line no-console
      console.log("CHATWOOT INIT", token);

      window.chatwootSettings = {
        hideMessageBubble: true,
        position: "right",
        locale: currentLanguage?.toLowerCase(),
        type: "standard",
      };

      setTokenForChat();

      addTag();

      window.addEventListener("chatwoot:ready", onReady);
    } else {
      // eslint-disable-next-line no-console
      console.log("SET TOKEN");
      setTokenForChat();
    }

    return () => {
      window.removeEventListener("chatwoot:ready", onReady);
      window.removeEventListener("chatwoot:close", onClose);
      // removeTag();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isInit, isAuthenticated]);

  return isLoading ? <ChatwootLoading /> : null;
};

export default ChatwootWidget;
